import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { Pencil, Trash2 } from 'lucide-react';
import {
  ErrorState,
  FilterChip,
  SearchField,
  SegmentedControl,
  TableCard,
  TableEmptyRow,
  TableSkeletonRows,
  Toolbar,
} from '../../components/ui';
import { sbg } from '../../theme/sbgTokens';
import { formatCount, formatDateRange } from '../../utils/format';
import { TimeOffStatus, TimeOffType } from '../../types/timeOff';
import type { TimeOffEntry } from '../../types/timeOff';
import { CATEGORY_LABELS, EntryStatusPill, hrs, typeLabel, TypeLabel } from './meta';
import { describeEntryText } from './text';

type TypeFilter = 'all' | TimeOffType;

const STATUS_FILTERS: { value: TimeOffStatus; label: string }[] = [
  { value: TimeOffStatus.PENDING, label: 'Pending' },
  { value: TimeOffStatus.APPROVED, label: 'Approved' },
  { value: TimeOffStatus.REJECTED, label: 'Rejected' },
];

const entryId = (entry: TimeOffEntry) => entry._id || (entry as any).id;

const categoryLabel = (entry: TimeOffEntry) =>
  entry.category ? CATEGORY_LABELS[entry.category] ?? entry.category : null;

/** Subject or the person's words first; import boilerplate and policy text become flags. */
const describe = (entry: TimeOffEntry) => {
  const text = describeEntryText(entry.notes, entry.originalCalendarSubject);
  const description = text.primary || categoryLabel(entry) || 'Time off entry';
  const category = categoryLabel(entry);
  const flags = [
    entry.isFromCalendar ? 'From calendar' : null,
    entry.reconciled ? 'Reconciled' : null,
    ...text.flags,
    category && category !== description ? category : null,
  ].filter((flag): flag is string => Boolean(flag));
  return { description, flags };
};

const TYPE_OPTIONS: { value: TypeFilter; label: string }[] = [
  { value: 'all', label: 'All types' },
  ...[TimeOffType.PTO, TimeOffType.SICK, TimeOffType.WFH].map((value) => ({ value, label: typeLabel(value) })),
];

/** Two lines, then an ellipsis; the full text stays in the DOM for screen readers and in the edit dialog. */
const CLAMP_2 = {
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  overflowWrap: 'anywhere',
} as const;

export interface HistoryTableProps {
  entries: TimeOffEntry[];
  firstName: string;
  canEdit: boolean;
  canDelete: boolean;
  loading: boolean;
  error: boolean;
  /** Why it failed and what to do, e.g. "The server didn't respond. Try again in a minute." */
  errorBody?: string;
  onRetry: () => void;
  onEdit: (entry: TimeOffEntry) => void;
  onDelete: (id: string) => void;
}

/** Every time-off entry on file, newest first, with client-side search and filters. */
const HistoryTable: React.FC<HistoryTableProps> = ({
  entries,
  firstName,
  canEdit,
  canDelete,
  loading,
  error,
  errorBody = "The server didn't respond. Try again in a minute.",
  onRetry,
  onEdit,
  onDelete,
}) => {
  const [search, setSearch] = useState('');
  const [type, setType] = useState<TypeFilter>('all');
  const [statuses, setStatuses] = useState<TimeOffStatus[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const sorted = useMemo(
    () => [...entries].sort((a, b) => b.startDate.localeCompare(a.startDate) || (b.endDate || '').localeCompare(a.endDate || '')),
    [entries]
  );

  const query = search.trim().toLowerCase();
  const scoped = useMemo(
    () => sorted.filter((entry) => {
      if (type !== 'all' && entry.type !== type) return false;
      if (!query) return true;
      // Search what the row shows, not the import boilerplate every calendar entry shares.
      const { description, flags } = describe(entry);
      return [description, ...flags, entry.reason, typeLabel(entry.type)]
        .some((value) => typeof value === 'string' && value.toLowerCase().includes(query));
    }),
    [sorted, type, query]
  );
  const filtered = useMemo(
    () => (statuses.length === 0 ? scoped : scoped.filter((entry) => statuses.includes(entry.status))),
    [scoped, statuses]
  );
  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    scoped.forEach((entry) => {
      counts[entry.status] = (counts[entry.status] || 0) + 1;
    });
    return counts;
  }, [scoped]);

  // A status with no entries at all would only lead to an empty table, so its chip stays hidden
  // unless selected. Judged on every entry, so chips don't come and go while someone types.
  const visibleStatusFilters = useMemo(
    () => STATUS_FILTERS.filter(
      (option) => statuses.includes(option.value) || entries.some((entry) => entry.status === option.value)
    ),
    [entries, statuses]
  );

  const hasFilters = Boolean(query) || type !== 'all' || statuses.length > 0;
  const lastPage = Math.max(0, Math.ceil(filtered.length / rowsPerPage) - 1);
  const currentPage = Math.min(page, lastPage);
  const pageRows = filtered.slice(currentPage * rowsPerPage, currentPage * rowsPerPage + rowsPerPage);
  const showActions = canEdit;
  const columnCount = showActions ? 6 : 5;

  const clearFilters = () => {
    setSearch('');
    setType('all');
    setStatuses([]);
    setPage(0);
  };

  const toggleStatus = (status: TimeOffStatus) => {
    setStatuses((current) => (current.includes(status) ? current.filter((value) => value !== status) : [...current, status]));
    setPage(0);
  };

  const toolbar = (
    <Toolbar
      end={
        !loading && !error ? (
          <Typography variant="body2" component="p" sx={{ color: sbg.text.secondary, fontVariantNumeric: 'tabular-nums' }}>
            {hasFilters
              ? `${formatCount(filtered.length, 'entry', 'entries')} of ${formatCount(entries.length, 'entry', 'entries')}`
              : formatCount(entries.length, 'entry', 'entries')}
          </Typography>
        ) : undefined
      }
    >
      <SearchField
        label="Search notes and subjects"
        placeholder="Search notes and subjects"
        value={search}
        onChange={(value) => {
          setSearch(value);
          setPage(0);
        }}
        width={260}
      />
      <SegmentedControl<TypeFilter>
        size="small"
        aria-label="Time-off type"
        value={type}
        onChange={(value) => {
          setType(value);
          setPage(0);
        }}
        options={TYPE_OPTIONS}
      />
      {visibleStatusFilters.length > 0 && (
        <Box role="group" aria-label="Status" sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
          {visibleStatusFilters.map((option) => (
            <FilterChip
              key={option.value}
              selected={statuses.includes(option.value)}
              onClick={() => toggleStatus(option.value)}
              count={statusCounts[option.value] || 0}
            >
              {option.label}
            </FilterChip>
          ))}
        </Box>
      )}
    </Toolbar>
  );

  return (
    <TableCard
      aria-label="Time-off history"
      toolbar={toolbar}
      footer={
        !loading && !error && filtered.length > 25 ? (
          <TablePagination
            component="div"
            count={filtered.length}
            page={currentPage}
            onPageChange={(_, next) => setPage(next)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(Number(event.target.value));
              setPage(0);
            }}
            rowsPerPageOptions={[25, 50, 100]}
            labelRowsPerPage="Rows per page"
          />
        ) : undefined
      }
    >
      <Table sx={{ minWidth: 760 }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: 200 }}>Dates</TableCell>
            <TableCell sx={{ width: 120 }}>Type</TableCell>
            <TableCell>Description</TableCell>
            <TableCell align="right" sx={{ width: 90 }}>Hours</TableCell>
            <TableCell sx={{ width: 120 }}>Status</TableCell>
            {showActions && (
              <TableCell align="right" sx={{ width: 96 }}>
                <span className="sbg-sr-only">Actions</span>
              </TableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <TableSkeletonRows columns={columnCount} />
          ) : error ? (
            <TableRow sx={{ '&&:hover': { bgcolor: 'transparent' } }}>
              <TableCell colSpan={columnCount} sx={{ borderBottom: 0 }}>
                <ErrorState
                  compact
                  title="Couldn't load time-off history"
                  body={errorBody}
                  onRetry={onRetry}
                />
              </TableCell>
            </TableRow>
          ) : entries.length === 0 ? (
            <TableEmptyRow
              colSpan={columnCount}
              title="No time off on file"
              body={`PTO, sick time and WFH for ${firstName}, from requests and calendar sync, appear here.`}
            />
          ) : filtered.length === 0 ? (
            <TableEmptyRow
              colSpan={columnCount}
              title="No entries match these filters"
              body="Try a different search, type or status."
              action={
                <Button variant="outlined" size="small" onClick={clearFilters}>
                  Clear filters
                </Button>
              }
            />
          ) : (
            pageRows.map((entry, index) => {
              const id = entryId(entry);
              const dates = formatDateRange(entry.startDate, entry.endDate);
              const { description, flags } = describe(entry);
              return (
                <TableRow
                  key={id || `${entry.startDate}-${index}`}
                  hover={canEdit}
                  tabIndex={canEdit ? 0 : undefined}
                  onClick={canEdit ? () => onEdit(entry) : undefined}
                  onKeyDown={
                    canEdit
                      ? (event) => {
                          if (event.target !== event.currentTarget) return;
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            onEdit(entry);
                          }
                        }
                      : undefined
                  }
                  sx={{ cursor: canEdit ? 'pointer' : 'default' }}
                >
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{dates}</TableCell>
                  <TableCell>
                    <TypeLabel type={entry.type} />
                  </TableCell>
                  <TableCell sx={{ maxWidth: 360 }}>
                    <Typography variant="body1" component="div" sx={{ color: sbg.text.primary, ...CLAMP_2 }}>
                      {description}
                    </Typography>
                    {flags.length > 0 && (
                      <Typography variant="body2" component="div" sx={{ color: sbg.text.secondary }}>
                        {flags.join(' · ')}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{hrs(entry.hours)}</TableCell>
                  <TableCell>
                    <EntryStatusPill status={entry.status} />
                  </TableCell>
                  {showActions && (
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap', py: 0 }}>
                      <Box sx={{ display: 'inline-flex', gap: 0.5 }}>
                        <Tooltip title="Edit entry">
                          <IconButton
                            size="small"
                            aria-label={`Edit entry, ${dates}`}
                            onClick={(event) => {
                              event.stopPropagation();
                              onEdit(entry);
                            }}
                          >
                            <Pencil size={16} />
                          </IconButton>
                        </Tooltip>
                        {canDelete && (
                          <Tooltip title="Delete entry">
                            <IconButton
                              size="small"
                              aria-label={`Delete entry, ${dates}`}
                              onClick={(event) => {
                                event.stopPropagation();
                                onDelete(id);
                              }}
                              sx={{ '&:hover': { color: sbg.status.danger.text, bgcolor: sbg.status.danger.bg } }}
                            >
                              <Trash2 size={16} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  )}
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </TableCard>
  );
};

export default HistoryTable;
